/**
 * Client store for the Reservation screen.
 *
 * Holds the booking list and the list's UI state (tab, search, page, page size,
 * filters, date range). Rows are read from `GET reservations/` by the page and
 * hydrated here by `ReservationInitializer` — the slice itself never fetches.
 *
 * **Every facet on this screen is server-side.** What lives here is the
 * *intent*; `ReservationQuerySync` mirrors it into the URL, the Server
 * Component re-reads, and the slice is re-seeded with the matching page.
 * Filtering the ten rows already on screen would search one page of a much
 * longer diary and disagree with the `count` the paginator prints.
 *
 * Per docs/frontend-conventions.md §9, feature-wide UI state belongs in Redux,
 * not component `useState`.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  Reservation,
  ReservationPage,
} from "@/components/reservation/types";

/**
 * Facets the Filter popover applies. Empty string means "no filter", which
 * keeps each facet a plain single-select — and, for `partySize`, an omitted
 * param, since an unknown `party_size` value is a **400**, not an empty page.
 */
export interface ReservationFilters {
  /**
   * One of the seven wire statuses, or `""` for "everything".
   *
   * **This is the same axis the tab strip writes to**, deliberately: the API
   * takes a single `?status=`, so a tab and a popover select that could
   * disagree would be two controls fighting over one parameter. The tabs are
   * the shortcut, the popover reaches the statuses the tabs do not show, and
   * both land here.
   *
   * `""` is not "no filter" on the wire — it is `?status=`, an **empty** value,
   * which is the only escape hatch that reveals drafts. Omitting the parameter
   * entirely is what hides them, and that is the API's default rather than
   * anything this screen asks for.
   */
  status: string;
  /** `1-2` · `3-4` · `5-8` · **`9plus`** — the value, never the label. */
  partySize: string;
}

/**
 * The list state the URL carries — what `ReservationInitializer` adopts on
 * mount. Mirrors the search params `ReservationQuerySync` writes, so the two
 * halves of the round trip cannot drift.
 */
export interface ReservationListQuery {
  search: string;
  filters: ReservationFilters;
  page: number;
  pageSize: number;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_RESERVATION_FILTERS: ReservationFilters = {
  status: "",
  partySize: "",
};

export interface ReservationState {
  items: Reservation[];
  /** Total rows across all pages of the filtered set — drives "1-10 of 180". */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  filters: ReservationFilters;
  /**
   * The header's date-range control, as ISO `yyyy-MM-dd`. `""` means unset.
   *
   * Unlike Stock's, this one is honoured: the list takes `date_from` /
   * `date_to` as an inclusive range, so the control filters the table rather
   * than merely riding the URL.
   */
  dateFrom: string;
  dateTo: string;
  /** A list navigation is in flight — see `setReservationListPending`. */
  listPending: boolean;
}

const initialState: ReservationState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  search: "",
  filters: EMPTY_RESERVATION_FILTERS,
  dateFrom: "",
  dateTo: "",
  listPending: false,
};

const reservationSlice = createSlice({
  name: "reservation",
  initialState,
  reducers: {
    /**
     * True from the moment a query change starts navigating until the server
     * render lands. Set by `ReservationQuerySync`, which owns the transition;
     * read by the table, which is a sibling and cannot see it any other way.
     */
    setReservationListPending(state, action: PayloadAction<boolean>) {
      state.listPending = action.payload;
    },
    /** Replace the list with a server-rendered page (called by the Initializer). */
    setReservations(state, action: PayloadAction<ReservationPage>) {
      state.items = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    /**
     * Adopt the URL's list state verbatim, on mount.
     *
     * **Not the individual setters**, which reset `page` to 1 — right when a
     * user changes a facet, wrong here: it would drop anyone landing on
     * `?page=3&status=confirmed` back to the first page.
     *
     * Without this the URL is write-only: `ReservationQuerySync` rebuilds the
     * query from this slice, so a fresh store — a reload, or a link someone
     * shared — wipes the params the page was just rendered with. The date range
     * rides along because this list genuinely honours it, unlike Stock's.
     */
    setReservationListQuery(
      state,
      action: PayloadAction<ReservationListQuery>,
    ) {
      state.search = action.payload.search;
      state.filters = action.payload.filters;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.dateFrom = action.payload.dateFrom;
      state.dateTo = action.payload.dateTo;
    },
    setReservationPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setReservationPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setReservationSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    /** What the tab strip writes — the same axis the popover's status select uses. */
    setReservationStatus(state, action: PayloadAction<string>) {
      state.filters.status = action.payload;
      state.page = 1;
    },
    setReservationFilters(state, action: PayloadAction<ReservationFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    /**
     * The header's date range. Kept apart from `filters`: it is the screen's
     * global control, not one of the table's facets, so the filter popover's
     * Reset must not clear it.
     */
    setReservationDateRange(
      state,
      action: PayloadAction<{ from: string; to: string }>,
    ) {
      state.dateFrom = action.payload.from;
      state.dateTo = action.payload.to;
      state.page = 1;
    },
  },
});

export const {
  setReservationListPending,
  setReservations,
  setReservationListQuery,
  setReservationPage,
  setReservationPageSize,
  setReservationSearch,
  setReservationStatus,
  setReservationFilters,
  setReservationDateRange,
} = reservationSlice.actions;

export default reservationSlice.reducer;
